import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, timer, switchMap, takeWhile, map, last, filter } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Largest file we send to AI analysis. Above it the AI step is skipped, but the
 * file still attaches. The backend shrinks big scans itself (a 71 MB scan was
 * tested); its real limit is page count, which it enforces. This only stops an
 * absurd upload before a pointless round trip.
 */
export const AI_MAX_FILE_BYTES = 104857600; // 100 MiB

export const AI_FILE_TOO_LARGE_MESSAGE =
  'This file is too large for AI analysis. You can still attach it and enter the details manually.';

/**
 * Machine-readable reason the backend gives with status 'failed'. Records
 * written before it existed come back as UNKNOWN or with no code at all.
 */
export type AnalysisErrorCode =
  | 'PDF_TOO_LARGE'
  | 'PDF_TOO_MANY_PAGES'
  | 'PDF_UNREADABLE'
  | 'AI_REJECTED'
  | 'AI_UNAVAILABLE'
  | 'ANALYSIS_TIMEOUT'
  | 'WORKER_FAILED'
  | 'UNKNOWN';

export interface AnalysisFailure {
  message: string;
  /** Whether trying again could plausibly succeed. */
  canRetry: boolean;
}

/**
 * What to tell the user when an analysis fails. Never show `errorMessage` itself:
 * it is for support and logs, and can be raw AWS text — "The JSON object in the
 * request body is malformed" is what a customer once saw for a file that was
 * simply too big.
 */
export function describeAnalysisFailure(errorCode?: string | null): AnalysisFailure {
  switch (errorCode) {
    case 'PDF_TOO_LARGE':
      return { message: AI_FILE_TOO_LARGE_MESSAGE, canRetry: false };
    case 'PDF_TOO_MANY_PAGES':
      return { message: 'AI analysis supports up to 600 pages. You can still attach this file and enter the details manually.', canRetry: false };
    case 'PDF_UNREADABLE':
      return { message: "We couldn't open this PDF. It may be damaged, password-protected, or not really a PDF.", canRetry: false };
    case 'AI_REJECTED':
      return { message: "The AI service couldn't process this document. Please enter the details manually.", canRetry: false };
    case 'AI_UNAVAILABLE':
      return { message: 'The AI service is busy right now. Please try again in a few minutes.', canRetry: true };
    case 'ANALYSIS_TIMEOUT':
      return { message: 'The analysis took too long. Please try again.', canRetry: true };
    default:
      return { message: 'AI analysis is not available right now. Please try again later or enter the details manually.', canRetry: true };
  }
}

/** "Analyzing contract… 1:05" — elapsed time reads better than "(22/110)" on a long wait. */
export function analysisProgressMessage(label: string, attempt: number, intervalMs: number): string {
  const seconds = Math.round(((attempt - 1) * intervalMs) / 1000);
  const elapsed = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  return seconds < 30 ? `${label}…` : `${label}… ${elapsed} (large scans can take a few minutes)`;
}

/**
 * Analysis status types
 */
export type AnalysisStatus = 'processing' | 'complete' | 'cached' | 'failed' | 'not_found' | 'error';

/**
 * Response from the AI document analysis endpoints
 */
export interface DocumentAnalysisResponse {
  status: AnalysisStatus;
  analyzedAt?: string;
  contractId?: string;
  message?: string;           // Present when processing or not_found
  extraction?: DocumentExtraction;
  error?: string;             // Legacy error field
  errorMessage?: string;      // Present when failed — support/logs only, never shown raw
  errorCode?: AnalysisErrorCode | string;
  inputPrepared?: 'original' | 'downsampled';
}

/**
 * Generic extracted value with confidence score
 */
export interface ExtractedValueWithConfidence<T> {
  value: T;
  confidence: number;
}

/**
 * Extracted data from AI analysis
 */
export interface DocumentExtraction {
  // Phase 1 fields
  parties?: ExtractedParty[];
  effectiveDate?: ExtractedField;
  terminationNoticePeriod?: ExtractedNoticePeriod;

  // Net payment terms — days from invoice/approval until payment is due
  // (e.g. "Net 30" → normalizedDays: 30). Distinct from terminationNoticePeriod
  // (how much notice to end the contract) and from compensation (how the party
  // is paid). Feeds Contract.paymentTerms → TimesheetSpec.netPaymentTerms → the
  // invoice due date. Only present when confidence is meaningful.
  paymentTermsPeriod?: ExtractedPaymentTerms;

  // Phase 2 fields
  summary?: string;
  overview?: ExtractedOverview;
  keySections?: ExtractedKeySections;

  // Auto-generated contract name: [Agreement Type] - [Vendor] - [Date]
  nameOfContract?: string;

  // Agreement type extracted from document header (e.g., "Professional Services Agreement")
  agreementType?: string;

  // Calculated expiration date with source and confidence
  calculatedExpirationDate?: CalculatedExpirationDate;

  // Estimated contract status based on dates and renewal terms
  estimatedStatus?: EstimatedStatus;

  // Renewal type (e.g., "Automatic - 1 Year", "Month to Month")
  // Only present if confidence >= 0.6
  renewalType?: ExtractedValueWithConfidence<string>;

  // Renewal term in months (e.g., 12, 24, 36)
  // Only present if confidence >= 0.6
  renewalTermMonths?: ExtractedValueWithConfidence<number | null>;

  // Contract type (e.g., "Professional Service Agreement")
  // Only present if confidence >= 0.8
  contractType?: ExtractedValueWithConfidence<string>;

  // Review period in days (from org-specific configuration)
  // Always reliable when present - no confidence threshold needed
  reviewPeriod?: number;

  // Embedded BAA detected in the contract PDF
  // true = contract contains a BAA as exhibit/addendum/attachment
  // false = no embedded BAA found
  embeddedBaa?: boolean;
}

/**
 * Overview tab extracted fields
 */
export interface ExtractedOverview {
  contractPurpose?: string;
  expirationDate?: ExtractedField;
  noticeToFacility?: string;
  noticeToVendor?: string;
  initialTerm?: ExtractedTerm;
}

/**
 * Term extraction with normalized months
 */
export interface ExtractedTerm {
  extracted: string;
  normalizedMonths?: number;
  confidence: number;
}

/**
 * Key contract sections for Review tab
 */
export interface ExtractedKeySections {
  term?: KeySection;              // Contract duration/term clause (not termination)
  indemnification?: KeySection;
  termination?: KeySection;
  nonCompete?: KeySection;
  paymentTerms?: KeySection;
  compensation?: KeySection;      // How the contracted party is paid (distinct from paymentTerms/invoicing)
  liabilityLimitations?: KeySection;
  insuranceRequirements?: KeySection;
  confidentiality?: KeySection;
  disputeResolution?: KeySection;
  costsFeesPricing?: KeySection;      // Price/fee schedules, cost & expense allocation (≠ compensation/paymentTerms)
  forceMajeure?: KeySection;          // Excusal of performance for events beyond a party's control
  governingLaw?: KeySection;          // Choice of law + jurisdiction/venue (≠ disputeResolution process)
  remedies?: KeySection;              // Remedies for breach/default: cure, damages, specific performance, injunctive relief
  warranty?: KeySection;              // Representations & warranties, and warranty disclaimers
}

/**
 * Individual key section extraction
 */
export interface KeySection {
  found: boolean;
  language?: string;
  pageReference?: string;
}

/**
 * Party extracted from document
 */
export interface ExtractedParty {
  name: string;
  role: 'facility' | 'vendor' | 'hospital' | 'unknown' | string;
  confidence: number;
  // Future: matchedParty for linking to known parties in system
  matchedParty?: {
    vendorNum: string;
    name: string;
    confidence: number;
  };
}

/**
 * Generic extracted field with confidence and source
 */
export interface ExtractedField {
  extracted: string;
  normalized?: string;
  confidence: number;
  sourceText?: string;
}

/**
 * Termination notice period extraction
 */
export interface ExtractedNoticePeriod {
  extracted: string;
  normalizedDays: number;
  confidence: number;
  sourceText?: string;
}

/**
 * Net payment-terms period. `normalizedDays` is a whole integer number of days
 * (e.g. "Net 30" → 30, "due upon receipt" → 0). Same shape as the notice period.
 */
export interface ExtractedPaymentTerms {
  extracted: string;
  normalizedDays: number;
  confidence: number;
  sourceText?: string;
}

/**
 * Calculated expiration date from AI analysis
 */
export interface CalculatedExpirationDate {
  date: string | null;        // YYYY-MM-DD format, or null if not determinable
  source: 'explicit' | 'calculated' | 'calculated_with_renewal' | 'evergreen' | 'expired' | 'unable_to_calculate';
  confidence: 'high' | 'medium' | 'low' | null;
  message: string | null;     // Human-readable context (show to user when present)
}

/**
 * Estimated contract status from AI analysis
 */
export interface EstimatedStatus {
  value: string | null;    // One of the valid statuses, or null if undetermined
  confidence: 'high' | 'medium' | 'low' | null;
}

/**
 * Valid contract status values that AI can return
 */
export const VALID_CONTRACT_STATUSES = [
  'Active - Initial Term',
  'Active - Auto Renewed',
  'Active - Perpetual',
  'Active Month to Month',
  'Expired'
] as const;

/**
 * Progress update emitted during polling
 */
export interface AnalysisProgress {
  status: AnalysisStatus;
  attempt: number;
  maxAttempts: number;
  message: string;
}

// ============================================================================
// INTAKE ANALYSIS INTERFACES (for new contract intake flow)
// ============================================================================

/**
 * Document type classification from generic intake analysis
 */
export type IntakeDocType = 'contract' | 'baa' | 'coi' | 'addendum' | 'unknown';

/**
 * Classification details nested inside extraction
 */
export interface IntakeClassification {
  docType: IntakeDocType;
  confidence: number;
  reasoning: string;
}

/**
 * BAA-specific extraction fields (when docType === 'baa')
 */
export interface BaaExtraction {
  coveredEntity?: string;
  businessAssociate?: string;
  typesOfService?: string[];
  initialTerm?: ExtractedTerm;
  renewalType?: string;
  description?: string;
  missingSignature?: boolean;
}

/**
 * COI-specific extraction fields (when docType === 'coi')
 */
export interface CoiExtraction {
  insuredParty?: string;
  insuranceProvider?: string;
  partyProvidingCoi?: string;
  coverageTypes?: string[];
  policyNumber?: string;
  description?: string;
}

/**
 * Addendum-specific extraction fields (when docType === 'addendum')
 */
export interface AddendumExtraction {
  parentDocumentReference?: string;
  amendmentSummary?: string;
  changedSections?: string[];
}

/**
 * Response from the intake AI analysis endpoints.
 * Now supports multi-document-type classification (contract, BAA, COI, addendum, unknown).
 * Backward compatible: when classification === 'contract', existing top-level extraction
 * fields (agreementType, keySections, calculatedExpirationDate, etc.) are still present.
 */
export interface IntakeAnalysisResponse {
  status: 'processing' | 'complete' | 'failed' | 'not_found';
  analysisId: string;
  fileName: string;
  analyzedAt?: string;
  message?: string;
  errorMessage?: string;      // support/logs only, never shown raw
  errorCode?: AnalysisErrorCode | string;
  /** 'downsampled' when the backend shrank an oversized scan before analysis. */
  inputPrepared?: 'original' | 'downsampled';

  /** Top-level classification shorthand */
  classification?: IntakeDocType;

  extraction?: DocumentExtraction & {
    /** Detailed classification info */
    classification?: IntakeClassification;

    /** Common fields from generic intake (may not exist on base DocumentExtraction) */
    expirationDate?: ExtractedField;

    /** Type-specific extraction blocks (only one populated based on docType) */
    contractFields?: {
      agreementType?: string;
      nameOfContract?: string;
      terminationNoticePeriod?: ExtractedNoticePeriod;
      initialTerm?: ExtractedTerm;
      renewalType?: string;
      embeddedBaa?: boolean;
      keySections?: ExtractedKeySections;
    };
    baaFields?: BaaExtraction;
    coiFields?: CoiExtraction;
    addendumFields?: AddendumExtraction;
  };

  partyMatches?: PartyMatch[];
}

/**
 * Party match result from fuzzy matching against vendors table
 */
export interface PartyMatch {
  extractedName: string;
  extractedRole: 'facility' | 'vendor' | 'legalEntity' | 'insurer' | 'unknown';
  extractedConfidence: number;
  matchConfidence: 'high' | 'medium' | 'low' | 'none';
  candidateCount: number;
  candidates: PartyMatchCandidate[];
}

/**
 * Individual candidate from entity matching (vendor or legal entity)
 */
export interface PartyMatchCandidate {
  entityType: 'legalEntity' | 'vendor';
  // Vendor fields (when entityType === 'vendor')
  vendorNum?: string;
  vendorName?: string;
  parentVendor?: string | null;
  // Legal entity fields (when entityType === 'legalEntity')
  legalEntityNum?: string;
  legalEntityName?: string;
  // Common fields
  location?: string | null;
  similarity: number;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentAnalysisService {
  private http = inject(HttpClient);

  // Configuration
  private readonly POLL_INTERVAL_MS = 3000;  // Poll every 3 seconds
  // ~5.5 min. The worker's own timeout is 5 min; typical large scans take 25–60 s.
  private readonly MAX_POLL_ATTEMPTS = 110;

  /**
   * Trigger AI analysis and poll for results.
   * The backend now uses async processing (SQS), so we trigger then poll.
   *
   * @param contractId The contract ID to analyze
   * @param progressCallback Optional callback to receive progress updates
   * @returns Observable with final analysis results or error
   */
  triggerAnalysis(contractId: string, progressCallback?: (progress: AnalysisProgress) => void, force: boolean = false): Observable<DocumentAnalysisResponse> {
    let triggerUrl = (environment as any).URL_DOCUMENT_ANALYSIS_TRIGGER?.replace('{contractId}', contractId);
    const statusUrl = (environment as any).URL_DOCUMENT_STATUS?.replace('{contractId}', contractId);

    if (!triggerUrl || !statusUrl) {
      console.error('[DocumentAnalysisService] API URLs not configured in environment');
      return of({ status: 'error', error: 'API endpoints not configured' } as DocumentAnalysisResponse);
    }

    // Bypass the cache and re-analyze (e.g. to pick up newly-added keySections).
    if (force) {
      triggerUrl = triggerUrl + (triggerUrl.includes('?') ? '&' : '?') + 'force=true';
    }

    // Step 1: Trigger the analysis
    return this.http.post<DocumentAnalysisResponse>(triggerUrl, {}).pipe(
      switchMap(triggerResponse => {
        // console.log('[DocumentAnalysisService] Trigger response:', triggerResponse);

        // If already complete (cached result), return immediately
        if (triggerResponse.status === 'complete' || triggerResponse.status === 'cached') {
          return of(triggerResponse);
        }

        // If failed immediately, return error
        if (triggerResponse.status === 'failed' || triggerResponse.status === 'error') {
          return of(triggerResponse);
        }

        // Step 2: Poll for results
        return this.pollForResults(statusUrl, progressCallback);
      }),
      catchError(error => {
        console.error('[DocumentAnalysisService] API error:', error);

        return of({
          status: 'error',
          error: error.message || 'Failed to analyze document'
        } as DocumentAnalysisResponse);
      })
    );
  }

  /**
   * Poll the status endpoint until analysis is complete or failed
   */
  private pollForResults(statusUrl: string, progressCallback?: (progress: AnalysisProgress) => void): Observable<DocumentAnalysisResponse> {
    let attempt = 0;

    return timer(0, this.POLL_INTERVAL_MS).pipe(
      switchMap(() => {
        attempt++;
        // Emit progress update via callback (not to subscriber)
        if (progressCallback) {
          progressCallback({
            status: 'processing',
            attempt,
            maxAttempts: this.MAX_POLL_ATTEMPTS,
            message: analysisProgressMessage('Analyzing contract', attempt, this.POLL_INTERVAL_MS)
          });
        }

        return this.http.get<DocumentAnalysisResponse>(statusUrl);
      }),
      map(response => {
        // Add attempt info for timeout check
        return { ...response, _attempt: attempt };
      }),
      // Only emit when we have a final result (not processing) OR we've timed out
      takeWhile(response => {
        // Continue polling while status is 'processing' and under max attempts
        const shouldContinue = response.status === 'processing' && response._attempt < this.MAX_POLL_ATTEMPTS;
        return shouldContinue;
      }, true), // inclusive: emit the final value that caused takeWhile to stop
      // Only emit the LAST value (the final result that stopped the polling)
      last(),
      // Transform the final result
      map(response => {
        const { _attempt, ...cleanResponse } = response as DocumentAnalysisResponse & { _attempt?: number };

        // If we stopped due to timeout while still processing, return error
        if (cleanResponse.status === 'processing') {
          console.warn('[DocumentAnalysisService] Polling timed out');
          return {
            status: 'error',
            error: 'Analysis timed out. Please try again later.',
            errorCode: 'ANALYSIS_TIMEOUT'
          } as DocumentAnalysisResponse;
        }

        if (cleanResponse.status === 'failed') {
          console.error('[DocumentAnalysisService] Analysis failed:', cleanResponse.errorMessage);
        }

        return cleanResponse;
      }),
      catchError(error => {
        console.error('[DocumentAnalysisService] Polling error:', error);

        return of({
          status: 'error',
          error: error.message || 'Failed to check analysis status'
        } as DocumentAnalysisResponse);
      })
    );
  }

  /**
   * Get the current analysis status without triggering a new analysis
   */
  getAnalysisStatus(contractId: string): Observable<DocumentAnalysisResponse> {
    const statusUrl = (environment as any).URL_DOCUMENT_STATUS?.replace('{contractId}', contractId);

    if (!statusUrl) {
      return of({ status: 'error', error: 'Status URL not configured' } as DocumentAnalysisResponse);
    }

    return this.http.get<DocumentAnalysisResponse>(statusUrl).pipe(
      catchError(error => {
        return of({
          status: 'error',
          error: error.message || 'Failed to get analysis status'
        } as DocumentAnalysisResponse);
      })
    );
  }

  // ============================================================================
  // INTAKE ANALYSIS METHODS (for new contract intake flow)
  // ============================================================================

  /**
   * Trigger AI analysis for contract intake (before contract exists in system).
   * Analyzes a PDF uploaded to S3 and returns extracted data + party matches.
   *
   * @param s3FileUrl The S3 URL of the uploaded PDF
   * @param fileName The original filename
   * @param progressCallback Optional callback to receive progress updates
   * @param force If true, bypass cache and re-analyze (add ?force=true to URL)
   * @returns Observable with final intake analysis results
   */
  triggerIntakeAnalysis(
    s3FileUrl: string,
    fileName: string,
    progressCallback?: (progress: AnalysisProgress) => void,
    force: boolean = false
  ): Observable<IntakeAnalysisResponse> {
    let triggerUrl = (environment as any).URL_DOCUMENT_INTAKE_TRIGGER;
    const statusUrlTemplate = (environment as any).URL_DOCUMENT_INTAKE_STATUS;

    if (!triggerUrl || !statusUrlTemplate) {
      console.error('[DocumentAnalysisService] Intake API URLs not configured in environment');
      return of({
        status: 'failed',
        analysisId: '',
        fileName,
        errorMessage: 'Intake API endpoints not configured'
      } as IntakeAnalysisResponse);
    }

    // Add force parameter to bypass cache if requested
    if (force) {
      triggerUrl = triggerUrl + (triggerUrl.includes('?') ? '&' : '?') + 'force=true';
    }

    // Step 1: Trigger the analysis
    return this.http.post<IntakeAnalysisResponse>(triggerUrl, { s3FileUrl, fileName }).pipe(
      switchMap(triggerResponse => {
        // console.log('[DocumentAnalysisService] Intake trigger response:', triggerResponse);

        // If already complete (cached/deduplicated), return immediately
        if (triggerResponse.status === 'complete') {
          return of(triggerResponse);
        }

        // If failed immediately, return error
        if (triggerResponse.status === 'failed') {
          return of(triggerResponse);
        }

        // Step 2: Poll for results
        const statusUrl = statusUrlTemplate.replace('{analysisId}', triggerResponse.analysisId);
        return this.pollForIntakeResults(statusUrl, fileName, progressCallback);
      }),
      catchError(error => {
        console.error('[DocumentAnalysisService] Intake API error:', error);

        return of({
          status: 'failed',
          analysisId: '',
          fileName,
          errorMessage: error.message || 'Failed to analyze document'
        } as IntakeAnalysisResponse);
      })
    );
  }

  /**
   * Poll the intake status endpoint until analysis is complete or failed
   */
  private pollForIntakeResults(
    statusUrl: string,
    fileName: string,
    progressCallback?: (progress: AnalysisProgress) => void
  ): Observable<IntakeAnalysisResponse> {
    let attempt = 0;
    const maxAttempts = this.MAX_POLL_ATTEMPTS;

    return timer(0, this.POLL_INTERVAL_MS).pipe(
      switchMap(() => {
        attempt++;
        // Emit progress update via callback
        if (progressCallback) {
          progressCallback({
            status: 'processing',
            attempt,
            maxAttempts,
            message: analysisProgressMessage('Analyzing contract', attempt, this.POLL_INTERVAL_MS)
          });
        }

        return this.http.get<IntakeAnalysisResponse>(statusUrl);
      }),
      map(response => {
        return { ...response, _attempt: attempt };
      }),
      takeWhile(response => {
        const shouldContinue = response.status === 'processing' && response._attempt < maxAttempts;
        return shouldContinue;
      }, true),
      last(),
      map(response => {
        const { _attempt, ...cleanResponse } = response as IntakeAnalysisResponse & { _attempt?: number };

        if (cleanResponse.status === 'processing') {
          console.warn('[DocumentAnalysisService] Intake polling timed out');
          return {
            status: 'failed',
            analysisId: cleanResponse.analysisId || '',
            fileName,
            errorMessage: 'Analysis timed out. Please try again later.',
            errorCode: 'ANALYSIS_TIMEOUT'
          } as IntakeAnalysisResponse;
        }

        if (cleanResponse.status === 'failed') {
          console.error('[DocumentAnalysisService] Intake analysis failed:', cleanResponse.errorMessage);
        }

        return cleanResponse;
      }),
      catchError(error => {
        console.error('[DocumentAnalysisService] Intake polling error:', error);

        return of({
          status: 'failed',
          analysisId: '',
          fileName,
          errorMessage: error.message || 'Failed to check analysis status'
        } as IntakeAnalysisResponse);
      })
    );
  }
}
